import { css } from 'aphrodite';
import * as React from 'react';
import {
	CreateSupportMailboxBody,
	ISupportMailbox,
	useCreateSupportMailboxMutation,
} from '../../../queries/Support/useSupportMailboxQuery';
import { getApiErrorMessage, isForbiddenError } from '../../../utils/Support/supportHelpers';
import { Button } from '../../../web/components-v2/Button';
import { Input } from '../../../web/components-v2/Input';
import { mailboxCreateFormStyles as styles } from './styles/mailboxCreateFormStyles';
import { settingsStyles as st } from './styles/settingsStyles';

interface IMailboxCreateFormProps {
	onCancel: () => void;
	onCreated: (mailbox: ISupportMailbox) => void;
}

/**
 * Create-mailbox form. Creation is an account-administrator-only operation; the
 * client cannot reliably know the caller's account role up front, so the form is
 * always available and a 403 from `POST support/mailboxes` is surfaced as a clear
 * "only account administrators can create mailboxes" message.
 */
export function MailboxCreateForm({ onCancel, onCreated }: IMailboxCreateFormProps) {
	const createMutation = useCreateSupportMailboxMutation();

	const [name, setName] = React.useState('');
	const [emailAddress, setEmailAddress] = React.useState('');
	const [fromName, setFromName] = React.useState('');
	const [replyToAddress, setReplyToAddress] = React.useState('');

	const isForbidden = createMutation.isError && isForbiddenError(createMutation.error);
	const canSubmit = name.trim().length > 0 && emailAddress.trim().length > 0;

	const handleCreate = () => {
		if (!canSubmit) return;
		// Only send fields accepted by the create endpoint — never `roundRobinAgentIds`.
		const body: CreateSupportMailboxBody = {
			name: name.trim(),
			emailAddress: emailAddress.trim(),
			fromName: fromName.trim() || undefined,
			replyToAddress: replyToAddress.trim() || undefined,
		};
		createMutation.mutate(body, {
			onSuccess: created => onCreated(created),
		});
	};

	return (
		<div className={css(st.formCard, styles.narrow)}>
			<h3 className={css(st.formTitle)}>New Mailbox</h3>

			{isForbidden ? (
				<div className={css(styles.forbiddenNotice)}>
					Only account administrators can create mailboxes. Ask an account administrator to set this up for you.
				</div>
			) : (
				createMutation.isError && (
					<div className={css(styles.errorMsg)}>
						{getApiErrorMessage(createMutation.error, 'Failed to create mailbox.')}
					</div>
				)
			)}

			<div className={css(st.field)}>
				<label className={css(st.fieldLabel)}>Mailbox Name</label>
				<Input
					type='text'
					value={name}
					onChange={e => setName(e.target.value)}
					placeholder='Support'
					styles={[st.input]}
				/>
			</div>

			<div className={css(st.field)}>
				<label className={css(st.fieldLabel)}>Email Address</label>
				<Input
					type='email'
					value={emailAddress}
					onChange={e => setEmailAddress(e.target.value)}
					placeholder='support@example.com'
					styles={[st.input]}
				/>
			</div>

			<div className={css(st.field)}>
				<label className={css(st.fieldLabel)}>From Name (optional)</label>
				<Input
					type='text'
					value={fromName}
					onChange={e => setFromName(e.target.value)}
					placeholder='Acme Support'
					styles={[st.input]}
				/>
			</div>

			<div className={css(st.field)}>
				<label className={css(st.fieldLabel)}>Reply-To Address (optional)</label>
				<Input
					type='email'
					value={replyToAddress}
					onChange={e => setReplyToAddress(e.target.value)}
					placeholder='no-reply@example.com'
					styles={[st.input]}
				/>
			</div>

			<div className={css(st.formActionsTight)}>
				<Button onClick={handleCreate} disabled={createMutation.isPending || !canSubmit || isForbidden}>
					{createMutation.isPending ? 'Creating...' : 'Create Mailbox'}
				</Button>
				<Button variant='outline' onClick={onCancel}>
					Cancel
				</Button>
			</div>
		</div>
	);
}
